import { Chart, ChartNodeData } from "@/components/chart/Chart.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { RightPaneLayout } from "@/components/layout/RightPaneLayout.tsx";
import { Page } from "@skailer-com/uikit";
import useEmployeesChartHook from "@/hooks/use-employees-chart.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  ApiEmployeeChartFilter,
  ApiEmployeeChartItem,
  EmployeeNodeMode,
} from "@/pages/employees/chart/employees-chart.types.ts";
import { EmployeeViewRightPane } from "@/pages/employees/widgets/EmployeeViewRightPane.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export function EmployeesChart() {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const [chart, setChart] = useState<ChartNodeData<ApiEmployeeChartItem>[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set([]));
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const { renderEmployeeName } = useFormatter();
  const [searchParams] = useSearchParams();
  const { loadEmployees, loadEmployeesChartByNodeId } = useEmployeesChartHook();
  const { canCurrentEmployeeViewField } = usePermissions();

  useTitle(t("core:org_chart_employees"));

  const library = filtersLibrary();

  const baseEmployeeFilters = [
    library.division_ids,
    library.location_ids,
    library.employment_type_ids,
    library.employment_contract_ids,
    library.legal_entity_ids,
    ...(canCurrentEmployeeViewField(EmployeeFieldPermission.employee_lifecycle_status)
      ? [
          {
            ...library.employee_lifecycle_statuses,
            hideInFilter: true,
          },
        ]
      : []),
  ];

  const {
    filter: { filter },
    filterButton,
    isFilterReady,
  } = useAppFilter<ApiEmployeeChartFilter>({
    filters: [...baseEmployeeFilters],
    source: AnalyticReportTypes.employeesChart,
  });

  const appendEmployeesToChart = useCallback((employees: ApiEmployeeChartItem[]) => {
    setChart((prevChart) => {
      const newEntitiesIds: Set<number | undefined> = new Set(
        employees.map((item) => item?.employee.id)
      );

      const prev = [...prevChart]
        .filter((item) => item.original && !newEntitiesIds.has(item.original.employee.id))
        .map((item) => item.original);

      const mergedItems = [...prev, ...employees];
      const nodes: ChartNodeData<ApiEmployeeChartItem>[] = [];
      const mergedIds: Set<number | undefined> = new Set(
        mergedItems.map((item) => item?.employee.id)
      );
      let needRoot = false;
      let countRootElements = 0;

      mergedItems.forEach((employeeChart) => {
        if (!employeeChart) return;

        const parentIdNumber: number | null =
          employeeChart.employee.employee_position?.manager?.id ?? null;

        if (!parentIdNumber) {
          needRoot = true;
          countRootElements++;
        }
      });

      mergedItems.forEach((employeeChart) => {
        if (!employeeChart) return;

        const parentIdNumber: number | null =
          employeeChart.employee.employee_position?.manager?.id ?? null;

        let parentId: string | null = parentIdNumber ? `${parentIdNumber}` : "root";
        let isExpandUpNode = false;

        if (parentIdNumber && !mergedIds.has(parentIdNumber) && !needRoot) {
          parentId = null;
          isExpandUpNode = true;
        }

        nodes.push({
          title: renderEmployeeName(employeeChart.employee),
          caption: getEmployeePositionTitle(employeeChart.employee),
          imgUrl: getEmployeeAvatarUrl(employeeChart.employee, "md"),
          id: `${employeeChart.employee.id}`,
          parentId,
          count: employeeChart.direct_subordinates_count,
          isExpandUpNode,
          isRoot: false,
          original: employeeChart,
          extra:
            employeeChart.all_subordinates_count > 0 &&
            employeeChart.all_subordinates_count > employeeChart.direct_subordinates_count
              ? t("plurals:employees_count", { count: employeeChart.all_subordinates_count })
              : undefined,
        });
      });

      if (needRoot) {
        nodes.push({
          title: employee?.organization?.title ?? t("common:company"),
          imgUrl: employee?.organization?.logo_url ?? "/favicon.svg",
          id: "root",
          parentId: null,
          isExpandUpNode: false,
          count: countRootElements,
          isRoot: true,
        });
      }

      return nodes;
    });
  }, []);

  useEffect(() => {
    if (!isFilterReady) return;

    const initializeChart = async () => {
      setChart([]);
      const employeeIdParam = searchParams.get("employee_id");
      const employeeId = employeeIdParam ? Number(employeeIdParam) : undefined;
      const items = await loadEmployees(
        employeeId,
        filter,
        employeeId ? EmployeeNodeMode.parent : EmployeeNodeMode.children
      );

      if (items) appendEmployeesToChart(items);

      if (employeeId) {
        const opened: string[] = [];
        const current: ApiEmployeeChartItem | undefined = items?.find(
          (employee) => employee.employee.id === employeeId
        );
        const parentId = current?.employee.employee_position?.manager?.id;

        opened.push(`${employeeId}`);
        if (current && parentId) opened.push(`${parentId}`);

        setExpanded(new Set(opened));

        if (items?.some((item) => !item.employee.employee_position?.manager)) {
          const rootManagerId = current?.employee.employee_position?.manager?.id;

          const rootItems = await loadEmployees(undefined, filter);

          if (rootItems) appendEmployeesToChart(rootItems);

          if (rootManagerId) {
            const rootItems = await loadEmployees(rootManagerId, filter);

            if (rootItems) appendEmployeesToChart(rootItems);
          }
        }
      } else {
        const rootEmployees =
          items?.filter((chartItem) => !chartItem.employee.employee_position?.manager) ?? [];
        const [ceo] = rootEmployees;

        if (rootEmployees.length === 1 && ceo?.direct_subordinates_count > 0) {
          const ceoId = ceo.employee.id;

          setExpanded(new Set([`${ceoId}`]));

          const directSubordinates = await loadEmployees(ceoId, filter, EmployeeNodeMode.children);

          if (directSubordinates) appendEmployeesToChart(directSubordinates);
        }
      }
    };

    initializeChart();
  }, [isFilterReady, searchParams]);

  const onTitleClick = useCallback((nodeId: string) => {
    if (nodeId == "root") return;

    const employeeId = Number(nodeId);
    if (Number.isNaN(employeeId)) return;

    setSelectedEmployeeId(employeeId);
  }, []);

  const onExpandClick = useCallback(
    (nodeId: string, needLoadData: boolean) => {
      if (!needLoadData) return;

      loadEmployeesChartByNodeId(nodeId, filter).then((employees) => {
        if (employees) appendEmployeesToChart(employees);
      });
    },
    [appendEmployeesToChart, filter, loadEmployeesChartByNodeId]
  );

  const onExpandUpClick = useCallback(
    (nodeId: string) => {
      loadEmployeesChartByNodeId(nodeId, filter, EmployeeNodeMode.parent).then((items) => {
        items.forEach((item) => {
          const managerId = item.employee.employee_position?.manager?.id;
          if (nodeId == `${item.employee.id}` && managerId) {
            setExpanded((prevState) => prevState.add(`${managerId}`));
          }
        });

        if (items) appendEmployeesToChart(items);

        if (!items.some((item) => !item.employee.employee_position?.manager)) return;

        const aimUpManager = items.find((item) => `${item.employee.id}` === nodeId);
        const rootManagerId = aimUpManager?.employee.employee_position?.manager?.id;

        loadEmployees(undefined, filter).then((rootItems) => {
          if (rootItems) appendEmployeesToChart(rootItems);
        });

        if (rootManagerId) {
          loadEmployees(rootManagerId, filter).then((rootManagerItems) => {
            if (rootManagerItems) appendEmployeesToChart(rootManagerItems);
          });
        }
      });
    },
    [appendEmployeesToChart, filter, loadEmployees, loadEmployeesChartByNodeId]
  );

  const hideRightPane = useCallback(() => {
    setSelectedEmployeeId(undefined);
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employees.viewedChart);
  });

  return (
    <Page variant="opaque" className={"h-full"}>
      <RightPaneLayout
        className={"h-full"}
        onHideRightPane={hideRightPane}
        rightPane={
          selectedEmployeeId
            ? (hidePane) => (
                <EmployeeViewRightPane employeeId={selectedEmployeeId} onClose={hidePane} />
              )
            : undefined
        }
      >
        <Page.Header title={t("core:org_chart_employees")}>{filterButton()}</Page.Header>
        {chart.length > 0 && (
          <Chart
            data={chart}
            onExpandClick={onExpandClick}
            onExpandUpClick={onExpandUpClick}
            onNodeClick={onTitleClick}
            expandedSet={expanded}
          />
        )}
      </RightPaneLayout>
    </Page>
  );
}
